import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, CheckCircle2, AlertCircle, ZoomIn, ZoomOut, Maximize, Sparkles, Share2, Linkedin, Twitter, Facebook, Mail, Copy, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { articles, sources, tierLabels, sentimentLabels } from '../lib/data';
type SocialPlatform = 'linkedin' | 'twitter' | 'facebook' | 'email';
function ShareDialog({
  article,
  open,
  onOpenChange
}: {
  article: (typeof articles)[0];
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [platform, setPlatform] = useState<SocialPlatform>('linkedin');
  const [copied, setCopied] = useState(false);
  const summary = `${article.title}\n\n${article.excerpt}\n\nFonte: ${article.source} — ${new Date(article.date).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })}`;
  const hashtags = '#MediaIntelligence #Esclapon';
  const platformTemplates: Record<SocialPlatform, string> = {
    linkedin: `📰 ${article.title}\n\n${article.excerpt}\n\nLeggi l'articolo completo su ${article.source}.\n\n${hashtags}`,
    twitter: `${article.title} — ${article.source}\n\n"${article.excerpt.slice(0, 140)}..."\n\n${hashtags}`,
    facebook: `${article.title}\n\n${article.excerpt}\n\nFonte: ${article.source}`,
    email: `Ciao,\n\nti segnalo questo articolo pubblicato su ${article.source}:\n\n"${article.title}"\n\n${article.excerpt}\n\nBuona lettura.`
  };
  const [editableText, setEditableText] = useState(platformTemplates.linkedin);
  useEffect(() => {
    setEditableText(platformTemplates[platform]);
    setCopied(false);
  }, [platform, article.id]);
  const platforms: {
    id: SocialPlatform;
    label: string;
    icon: React.ElementType;
    color: string;
    bg: string;
  }[] = [{
    id: 'linkedin',
    label: 'LinkedIn',
    icon: Linkedin,
    color: '#0A66C2',
    bg: '#0A66C2'
  }, {
    id: 'twitter',
    label: 'X / Twitter',
    icon: Twitter,
    color: '#000000',
    bg: '#000000'
  }, {
    id: 'facebook',
    label: 'Facebook',
    icon: Facebook,
    color: '#1877F2',
    bg: '#1877F2'
  }, {
    id: 'email',
    label: 'Email',
    icon: Mail,
    color: '#6B7280',
    bg: '#6B7280'
  }];
  const currentPlatform = platforms.find(p => p.id === platform)!;
  const CurrentIcon = currentPlatform.icon;
  const handleCopy = () => {
    navigator.clipboard.writeText(editableText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const charCount = editableText.length;
  const charLimit = platform === 'twitter' ? 280 : platform === 'linkedin' ? 3000 : null;
  return <Dialog open={open} onOpenChange={onOpenChange} data-id="element-376">
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto" data-id="element-377">
        <DialogHeader data-id="element-378">
          <DialogTitle className="flex items-center gap-2" data-id="element-379">
            <Share2 className="h-5 w-5 text-accent" data-id="element-380" />
            Condividi articolo
          </DialogTitle>
          <DialogDescription data-id="element-381">
            Scegli la piattaforma e personalizza il testo prima di pubblicare.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2" data-id="element-382">
          {/* Platform selector */}
          <div data-id="element-383">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block" data-id="element-384">
              Piattaforma
            </label>
            <div className="grid grid-cols-4 gap-2" data-id="element-385">
              {platforms.map(p => {
              const Icon = p.icon;
              const isActive = p.id === platform;
              return <button key={p.id} onClick={() => setPlatform(p.id)} className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-all ${isActive ? 'border-accent bg-accent/5 ring-1 ring-accent/30' : 'border-border bg-card hover:border-accent/40'}`} data-id="element-386">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white" style={{
                  backgroundColor: p.bg
                }} data-id="element-387">
                      <Icon className="h-4 w-4" data-id="element-388" />
                    </div>
                    <span className="text-xs font-medium" data-id="element-389">{p.label}</span>
                  </button>;
            })}
            </div>
          </div>

          {/* Preview card */}
          <div data-id="element-390">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block" data-id="element-391">
              Anteprima
            </label>
            <div className="border border-border rounded-xl overflow-hidden bg-white" data-id="element-392">
              {/* Header del post */}
              <div className="flex items-center gap-3 p-4 border-b border-border" data-id="element-393">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white flex-shrink-0" style={{
                backgroundColor: currentPlatform.bg
              }} data-id="element-394">
                  <CurrentIcon className="h-5 w-5" data-id="element-395" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-396">
                  <div className="font-semibold text-sm text-foreground" data-id="element-397">
                    Esclapon & Co.
                  </div>
                  <div className="text-xs text-muted-foreground" data-id="element-398">
                    {platform === 'email' ? 'Bozza email' : 'Post • Adesso'}
                  </div>
                </div>
              </div>

              {/* Corpo */}
              <div className="p-4" data-id="element-399">
                <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed" data-id="element-400">
                  {editableText}
                </div>
              </div>

              {/* Card articolo */}
              <div className="mx-4 mb-4 border border-border rounded-lg overflow-hidden bg-muted/20" data-id="element-401">
                <div className="px-4 py-3" data-id="element-402">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1" data-id="element-403">
                    {article.source}
                  </div>
                  <div className="text-sm font-semibold text-foreground line-clamp-2" data-id="element-404">
                    {article.title}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1.5" data-id="element-405">
                    {new Date(article.date).toLocaleDateString('it-IT', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Editable text */}
          <div data-id="element-406">
            <div className="flex items-center justify-between mb-2" data-id="element-407">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-408">
                Testo
              </label>
              {charLimit && <span className={`text-xs tabular-nums ${charCount > charLimit ? 'text-destructive font-medium' : 'text-muted-foreground'}`} data-id="element-409">
                  {charCount}/{charLimit}
                </span>}
            </div>
            <textarea value={editableText} onChange={e => setEditableText(e.target.value)} rows={6} className="w-full px-3 py-2.5 text-sm border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-accent resize-y" data-id="element-410" />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2" data-id="element-411">
            <Button variant="outline" onClick={handleCopy} className="rounded-full flex-1" data-id="element-412">
              {copied ? <>
                  <Check className="h-4 w-4 mr-2 text-emerald-600" data-id="element-413" />
                  Copiato
                </> : <>
                  <Copy className="h-4 w-4 mr-2" data-id="element-414" />
                  Copia testo
                </>}
            </Button>
            <Button className="rounded-full flex-1 text-white hover:opacity-90" style={{
            backgroundColor: currentPlatform.bg
          }} data-id="element-415">
              <CurrentIcon className="h-4 w-4 mr-2" data-id="element-416" />
              Pubblica su {currentPlatform.label}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
}
export function ArticleDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [shareOpen, setShareOpen] = useState(false);
  // In a real app, we'd fetch the article by ID. For now, we'll just use the first one if not found.
  const article = articles.find(a => a.id === id) || articles[0];
  const ConfidenceDot = ({
    level
  }: {
    level: 'high' | 'medium' | 'low';
  }) => {
    const colors = {
      high: 'bg-sentiment-positive',
      medium: 'bg-yellow-500',
      low: 'bg-sentiment-negative'
    };
    return <div className={`w-2 h-2 rounded-full ${colors[level]} flex-shrink-0`} title={`Confidence: ${level}`} data-id="element-417" />;
  };
  return <div className="h-full flex flex-col bg-background" data-id="element-418">
      {/* Header */}
      <div className="h-14 border-b border-border bg-card flex items-center justify-between px-2 md:px-4 flex-shrink-0" data-id="element-419">
        <div className="flex items-center gap-2 md:gap-4 min-w-0" data-id="element-420">
          <Button variant="ghost" size="icon-sm" onClick={() => navigate('/dashboard')} className="flex-shrink-0" data-id="element-421">
            <ChevronLeft className="h-4 w-4" data-id="element-422" />
          </Button>
          <div className="flex items-center gap-2 min-w-0" data-id="element-423">
            <Badge variant="outline" className="text-[10px] md:text-xs hidden sm:inline-flex flex-shrink-0" data-id="element-424">
              {article.source}
            </Badge>
            <span className="text-xs md:text-sm font-medium truncate max-w-[150px] sm:max-w-[200px] md:max-w-[300px]" data-id="element-425">
              {article.title}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-426">
          <Button variant="outline" size="sm" onClick={() => setShareOpen(true)} className="rounded-full h-8 px-3 text-xs md:text-sm border-accent text-accent hover:bg-accent/10 hover:text-accent" data-id="element-427">
            <Share2 className="h-3.5 w-3.5 mr-1.5" data-id="element-428" />
            Share
          </Button>
          <Badge variant="secondary" className="bg-blue-100 text-blue-700 text-[10px] md:text-xs" data-id="element-429">
            Da validare
          </Badge>
        </div>
      </div>

      {/* Score Sub-bar (desktop only) */}
      <div className="hidden md:flex items-center justify-between px-6 py-3 border-b border-border bg-muted/10 flex-shrink-0" data-id="element-430">
        <div className="flex items-center gap-8" data-id="element-431">
          <div className="flex items-center gap-2" data-id="element-432">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-433">
              Score Rilevanza
            </span>
            <span className="text-2xl font-bold text-accent" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-434">
              {article.relevanceScore.toFixed(1)}
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-435">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-436">
              AVE
            </span>
            <span className="text-lg font-semibold" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-437">
              €{article.ave.toLocaleString('it-IT')}
            </span>
          </div>
        </div>
        <Button className="h-10 px-6 text-sm font-medium rounded-full" onClick={() => navigate('/dashboard')} data-id="element-438">
          Approva e Prosegui
        </Button>
      </div>

      {/* Desktop Split View */}
      <div className="hidden md:flex flex-1 overflow-hidden" data-id="element-439">
        {/* Left: PDF Preview (60%) */}
        <div className="w-[60%] border-r border-border bg-muted/30 flex flex-col" data-id="element-440">
          <div className="h-10 border-b border-border flex items-center justify-between px-4 bg-card" data-id="element-441">
            <span className="text-xs font-medium text-muted-foreground" data-id="element-442">
              Pagina {article.page} di 24
            </span>
            <div className="flex items-center gap-1" data-id="element-443">
              <Button variant="ghost" size="icon-sm" data-id="element-444">
                <ZoomOut className="h-4 w-4" data-id="element-445" />
              </Button>
              <span className="text-xs font-medium w-12 text-center" data-id="element-446">100%</span>
              <Button variant="ghost" size="icon-sm" data-id="element-447">
                <ZoomIn className="h-4 w-4" data-id="element-448" />
              </Button>
              <Separator orientation="vertical" className="h-4 mx-1" data-id="element-449" />
              <Button variant="ghost" size="icon-sm" data-id="element-450">
                <Maximize className="h-4 w-4" data-id="element-451" />
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-auto p-8 flex justify-center" data-id="element-452">
            {/* Simulated PDF Page */}
            <div className="w-[800px] bg-white shadow-sm border border-border relative" data-id="element-453">
              {/* Newspaper image */}
              <img src="https://cdn.magicpatterns.com/uploads/4FPWNcM3ZiRtHEZFBzjbKc/Screenshot_2026-04-22_alle_14.51.37.png" alt="Articolo di giornale" className="w-full h-auto" data-id="element-454" />
              {/* Simulated Highlighted Clipping */}
              <div className="absolute top-[12%] left-[5%] w-[90%] h-[35%] bg-accent/10 border-2 border-accent/50 rounded-sm" data-id="element-455">
                <div className="absolute -top-6 left-0 bg-accent text-white text-xs px-2 py-1 rounded-sm font-medium flex items-center gap-1" data-id="element-456">
                  <Sparkles className="h-3 w-3" data-id="element-457" />
                  Clipping Rilevato
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Validation Form (40%) */}
        <div className="w-[40%] flex flex-col bg-card" data-id="element-458">
          <div className="flex-1 overflow-auto p-6 space-y-8" data-id="element-459">
            {/* AI Notice */}

            {/* Form Fields */}
            <div className="space-y-6" data-id="element-460">
              {/* Source & Date */}
              <div className="grid grid-cols-2 gap-4" data-id="element-461">
                <div className="space-y-2" data-id="element-462">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-463">
                    Testata <ConfidenceDot level="high" data-id="element-464" />
                  </label>
                  <Select defaultValue={article.source} data-id="element-465">
                    <SelectTrigger data-id="element-466">
                      <SelectValue data-id="element-467" />
                    </SelectTrigger>
                    <SelectContent data-id="element-468">
                      {sources.map(s => <SelectItem key={s.name} value={s.name} data-id="element-469">
                          {s.name}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2" data-id="element-470">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-471">
                    Data <ConfidenceDot level="high" data-id="element-472" />
                  </label>
                  <input type="date" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" defaultValue={article.date} data-id="element-473" />
                </div>
              </div>

              {/* Title */}
              <div className="space-y-2" data-id="element-474">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-475">
                  Titolo Articolo <ConfidenceDot level="medium" data-id="element-476" />
                </label>
                <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" defaultValue={article.title} data-id="element-477" />
              </div>

              {/* Author */}
              <div className="space-y-2" data-id="element-478">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-479">
                  Firma / Autore <ConfidenceDot level="high" data-id="element-480" />
                </label>
                <div className="flex items-center gap-4" data-id="element-481">
                  <input type="text" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" defaultValue={article.author} data-id="element-482" />
                  <div className="flex items-center gap-2 flex-shrink-0" data-id="element-483">
                    <Switch id="authoritative" defaultChecked={article.isAuthoritative} data-id="element-484" />
                    <label htmlFor="authoritative" className="text-sm font-medium" data-id="element-485">
                      Autorevole
                    </label>
                  </div>
                </div>
              </div>

              <Separator data-id="element-486" />

              {/* Content Type */}
              <div className="space-y-3" data-id="element-487">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-488">
                  Tipologia Contenuto <ConfidenceDot level="medium" data-id="element-489" />
                </label>
                <RadioGroup defaultValue={article.contentType} className="grid grid-cols-2 gap-2" data-id="element-490">
                  <div className="flex items-center space-x-2" data-id="element-491">
                    <RadioGroupItem value="dedicated_article" id="r1" data-id="element-492" />
                    <label htmlFor="r1" className="text-sm" data-id="element-493">
                      Articolo dedicato
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-494">
                    <RadioGroupItem value="interview" id="r2" data-id="element-495" />
                    <label htmlFor="r2" className="text-sm" data-id="element-496">
                      Intervista
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-497">
                    <RadioGroupItem value="signed_commentary" id="r3" data-id="element-498" />
                    <label htmlFor="r3" className="text-sm" data-id="element-499">
                      Commento firmato
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-500">
                    <RadioGroupItem value="mention" id="r4" data-id="element-501" />
                    <label htmlFor="r4" className="text-sm" data-id="element-502">
                      Citazione
                    </label>
                  </div>
                </RadioGroup>
              </div>

              <Separator data-id="element-503" />

              {/* Positioning */}
              <div className="space-y-3" data-id="element-504">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-505">
                  Posizionamento <ConfidenceDot level="low" data-id="element-506" />
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-507">
                  <div className="flex items-center space-x-2" data-id="element-508">
                    <Checkbox id="c1" defaultChecked={article.hasPhoto} data-id="element-509" />
                    <label htmlFor="c1" className="text-sm" data-id="element-510">
                      Foto presente
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-511">
                    <Checkbox id="c2" defaultChecked={article.titleMentionsClient} data-id="element-512" />
                    <label htmlFor="c2" className="text-sm" data-id="element-513">
                      Cliente nel titolo
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-514">
                    <Checkbox id="c3" defaultChecked={article.boldedReference} data-id="element-515" />
                    <label htmlFor="c3" className="text-sm" data-id="element-516">
                      Citazione in grassetto
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-517">
                    <Checkbox id="c4" defaultChecked={article.frontPageCallout} data-id="element-518" />
                    <label htmlFor="c4" className="text-sm" data-id="element-519">
                      Richiamo in prima
                    </label>
                  </div>
                </div>
              </div>

              <Separator data-id="element-520" />

              {/* Sentiment */}
              <div className="space-y-3" data-id="element-521">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-522">
                  Sentiment <ConfidenceDot level="high" data-id="element-523" />
                </label>
                <div className="flex gap-2" data-id="element-524">
                  <Button variant={article.sentiment === 'positive' ? 'default' : 'outline'} className={article.sentiment === 'positive' ? 'bg-sentiment-positive hover:bg-sentiment-positive/90' : ''} data-id="element-525">
                    Positivo
                  </Button>
                  <Button variant={article.sentiment === 'neutral' ? 'default' : 'outline'} className={article.sentiment === 'neutral' ? 'bg-sentiment-neutral hover:bg-sentiment-neutral/90' : ''} data-id="element-526">
                    Neutro
                  </Button>
                  <Button variant={article.sentiment === 'negative' ? 'default' : 'outline'} className={article.sentiment === 'negative' ? 'bg-sentiment-negative hover:bg-sentiment-negative/90' : ''} data-id="element-527">
                    Negativo
                  </Button>
                </div>
              </div>

              <Separator data-id="element-528" />

              {/* Score Breakdown */}
              <div className="space-y-3" data-id="element-529">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-530">
                  Dettaglio Algoritmo
                </label>
                <div className="bg-muted/30 border border-border rounded-md p-3" data-id="element-531">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs" data-id="element-532">
                    <div className="flex justify-between" data-id="element-533">
                      <span className="text-muted-foreground" data-id="element-534">
                        Tier testata:
                      </span>
                      <span className="font-medium" data-id="element-535">
                        +{article.scoreBreakdown.tier}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-536">
                      <span className="text-muted-foreground" data-id="element-537">
                        Posizionamento:
                      </span>
                      <span className="font-medium" data-id="element-538">
                        +{article.scoreBreakdown.positioning}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-539">
                      <span className="text-muted-foreground" data-id="element-540">Tipologia:</span>
                      <span className="font-medium" data-id="element-541">
                        +{article.scoreBreakdown.contentType}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-542">
                      <span className="text-muted-foreground" data-id="element-543">Sentiment:</span>
                      <span className="font-medium" data-id="element-544">
                        +{article.scoreBreakdown.sentiment}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Tabbed View */}
      <div className="md:hidden flex-1 flex flex-col overflow-hidden" data-id="element-545">
        <Tabs defaultValue="validation" className="flex-1 flex flex-col h-full" data-id="element-546">
          <div className="px-4 pt-4 pb-2 bg-card border-b border-border flex-shrink-0" data-id="element-547">
            <TabsList className="w-full grid grid-cols-2" data-id="element-548">
              <TabsTrigger value="pdf" className="text-xs" data-id="element-549">
                Anteprima PDF
              </TabsTrigger>
              <TabsTrigger value="validation" className="text-xs" data-id="element-550">
                Validazione
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="pdf" className="flex-1 m-0 overflow-auto bg-muted/30 p-4 flex justify-center" data-id="element-551">
            {/* Mobile Simulated PDF Page */}
            <div className="w-full max-w-[400px] aspect-[1/1.4] bg-white shadow-sm border border-border relative transform scale-100 origin-top" data-id="element-552">
              <div className="absolute top-[15%] left-[10%] w-[80%] h-[40%] bg-accent/10 border-2 border-accent/50 rounded-sm" data-id="element-553">
                <div className="absolute -top-5 left-0 bg-accent text-white text-[10px] px-1.5 py-0.5 rounded-sm font-medium flex items-center gap-1" data-id="element-554">
                  <Sparkles className="h-2 w-2" data-id="element-555" />
                  Clipping
                </div>
              </div>
              <div className="p-6 space-y-3 opacity-20" data-id="element-556">
                <div className="h-6 bg-gray-300 w-3/4 mb-4" data-id="element-557" />
                <div className="h-2 bg-gray-300 w-full" data-id="element-558" />
                <div className="h-2 bg-gray-300 w-full" data-id="element-559" />
                <div className="h-2 bg-gray-300 w-5/6" data-id="element-560" />
                <div className="h-2 bg-gray-300 w-full mt-4" data-id="element-561" />
                <div className="h-2 bg-gray-300 w-4/5" data-id="element-562" />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="validation" className="flex-1 m-0 overflow-auto flex flex-col bg-card" data-id="element-563">
            <div className="flex-1 p-4 space-y-6" data-id="element-564">
              {/* AI Notice */}
              <div className="bg-blue-50 border border-blue-100 rounded-md p-3 flex items-start gap-2" data-id="element-565">
                <Sparkles className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-566" />
                <div data-id="element-567">
                  <h4 className="text-xs font-medium text-blue-900" data-id="element-568">
                    Estrazione AI completata
                  </h4>
                  <p className="text-[10px] text-blue-700 mt-1" data-id="element-569">
                    I campi con il pallino verde hanno un'alta confidenza.
                  </p>
                </div>
              </div>

              {/* Form Fields - Mobile Layout */}
              <div className="space-y-5" data-id="element-570">
                <div className="grid grid-cols-2 gap-3" data-id="element-571">
                  <div className="space-y-1.5" data-id="element-572">
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-573">
                      Testata <ConfidenceDot level="high" data-id="element-574" />
                    </label>
                    <Select defaultValue={article.source} data-id="element-575">
                      <SelectTrigger className="h-9 text-xs" data-id="element-576">
                        <SelectValue data-id="element-577" />
                      </SelectTrigger>
                      <SelectContent data-id="element-578">
                        {sources.map(s => <SelectItem key={s.name} value={s.name} className="text-xs" data-id="element-579">
                            {s.name}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5" data-id="element-580">
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-581">
                      Data <ConfidenceDot level="high" data-id="element-582" />
                    </label>
                    <input type="date" className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background" defaultValue={article.date} data-id="element-583" />
                  </div>
                </div>

                <div className="space-y-1.5" data-id="element-584">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-585">
                    Titolo Articolo <ConfidenceDot level="medium" data-id="element-586" />
                  </label>
                  <textarea className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-2 py-2 text-xs ring-offset-background" defaultValue={article.title} data-id="element-587" />
                </div>

                <div className="space-y-1.5" data-id="element-588">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-589">
                    Firma / Autore <ConfidenceDot level="high" data-id="element-590" />
                  </label>
                  <div className="flex flex-col gap-2" data-id="element-591">
                    <input type="text" className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background" defaultValue={article.author} data-id="element-592" />
                    <div className="flex items-center gap-2" data-id="element-593">
                      <Switch id="authoritative-mobile" defaultChecked={article.isAuthoritative} className="scale-75 origin-left" data-id="element-594" />
                      <label htmlFor="authoritative-mobile" className="text-xs font-medium" data-id="element-595">
                        Autorevole
                      </label>
                    </div>
                  </div>
                </div>

                <Separator data-id="element-596" />

                <div className="space-y-2" data-id="element-597">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-598">
                    Tipologia Contenuto <ConfidenceDot level="medium" data-id="element-599" />
                  </label>
                  <RadioGroup defaultValue={article.contentType} className="grid grid-cols-1 gap-2" data-id="element-600">
                    <div className="flex items-center space-x-2" data-id="element-601">
                      <RadioGroupItem value="dedicated_article" id="rm1" className="w-3 h-3" data-id="element-602" />
                      <label htmlFor="rm1" className="text-xs" data-id="element-603">
                        Articolo dedicato
                      </label>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-604">
                      <RadioGroupItem value="interview" id="rm2" className="w-3 h-3" data-id="element-605" />
                      <label htmlFor="rm2" className="text-xs" data-id="element-606">
                        Intervista
                      </label>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-607">
                      <RadioGroupItem value="signed_commentary" id="rm3" className="w-3 h-3" data-id="element-608" />
                      <label htmlFor="rm3" className="text-xs" data-id="element-609">
                        Commento firmato
                      </label>
                    </div>
                  </RadioGroup>
                </div>

                <Separator data-id="element-610" />

                <div className="space-y-2" data-id="element-611">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-612">
                    Posizionamento <ConfidenceDot level="low" data-id="element-613" />
                  </label>
                  <div className="grid grid-cols-1 gap-2" data-id="element-614">
                    <div className="flex items-center space-x-2" data-id="element-615">
                      <Checkbox id="cm1" defaultChecked={article.hasPhoto} className="w-3 h-3" data-id="element-616" />
                      <label htmlFor="cm1" className="text-xs" data-id="element-617">
                        Foto presente
                      </label>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-618">
                      <Checkbox id="cm2" defaultChecked={article.titleMentionsClient} className="w-3 h-3" data-id="element-619" />
                      <label htmlFor="cm2" className="text-xs" data-id="element-620">
                        Cliente nel titolo
                      </label>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-621">
                      <Checkbox id="cm3" defaultChecked={article.boldedReference} className="w-3 h-3" data-id="element-622" />
                      <label htmlFor="cm3" className="text-xs" data-id="element-623">
                        Citazione in grassetto
                      </label>
                    </div>
                  </div>
                </div>

                <Separator data-id="element-624" />

                <div className="space-y-2" data-id="element-625">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-626">
                    Sentiment <ConfidenceDot level="high" data-id="element-627" />
                  </label>
                  <div className="flex gap-1.5" data-id="element-628">
                    <Button variant={article.sentiment === 'positive' ? 'default' : 'outline'} size="sm" className={`h-8 text-[10px] px-2 flex-1 ${article.sentiment === 'positive' ? 'bg-sentiment-positive hover:bg-sentiment-positive/90' : ''}`} data-id="element-629">
                      Positivo
                    </Button>
                    <Button variant={article.sentiment === 'neutral' ? 'default' : 'outline'} size="sm" className={`h-8 text-[10px] px-2 flex-1 ${article.sentiment === 'neutral' ? 'bg-sentiment-neutral hover:bg-sentiment-neutral/90' : ''}`} data-id="element-630">
                      Neutro
                    </Button>
                    <Button variant={article.sentiment === 'negative' ? 'default' : 'outline'} size="sm" className={`h-8 text-[10px] px-2 flex-1 ${article.sentiment === 'negative' ? 'bg-sentiment-negative hover:bg-sentiment-negative/90' : ''}`} data-id="element-631">
                      Negativo
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Bottom Score Card */}
            <div className="p-4 border-t border-border bg-muted/10 flex-shrink-0" data-id="element-632">
              <div className="flex items-end justify-between mb-3" data-id="element-633">
                <div data-id="element-634">
                  <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5" data-id="element-635">
                    Score Rilevanza
                  </div>
                  <div className="text-3xl font-bold text-accent" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-636">
                    {article.relevanceScore.toFixed(1)}
                  </div>
                </div>
                <div className="text-right" data-id="element-637">
                  <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5" data-id="element-638">
                    Valore AVE
                  </div>
                  <div className="text-lg font-semibold" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-639">
                    €{article.ave.toLocaleString('it-IT')}
                  </div>
                </div>
              </div>
              <Button className="w-full h-12 text-sm font-medium rounded-full" onClick={() => navigate('/dashboard')} data-id="element-640">
                Approva e Prosegui
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <ShareDialog article={article} open={shareOpen} onOpenChange={setShareOpen} data-id="element-641" />
    </div>;
}